import React, { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { pinSvgIconPink } from "assets/icons";
import { FormButton, CloseButton } from "reusableComponents";
import { transformBookingData } from "services/booking.services";
import { useSelector } from "react-redux";
import { selectAuthToken } from "store/auth/selectors";
import styles from "./styles.module.scss";
import { submitSpaceRating } from "services/placeNotes.services";
import { useModal } from "context";
import ReviewStarsInput from "./ReviewStarsInput";

// Rating categories configuration for better maintainability
const RATING_CATEGORIES = [
  {
    key: "qualityPrice",
    state: "qualityPriceRating",
    setter: "setQualityPriceRating",
    translationKey: "dashboard.ratings.ratingsQualityPriceQuestion",
  },
  {
    key: "location",
    state: "locationRating",
    setter: "setLocationRating",
    translationKey: "dashboard.ratings.ratingsLocationQuestion",
  },
  {
    key: "services",
    state: "servicesRating",
    setter: "setServicesRating",
    translationKey: "dashboard.ratings.ratingsServicesQuestion",
  },
  {
    key: "equipments",
    state: "equipmentsRating",
    setter: "setEquipmentsRating",
    translationKey: "dashboard.ratings.ratingsEquipmentsQuestion",
  },
];

const SpaceToRateCard = ({ booking, onRatingSubmitted = () => {} }) => {
  const { t } = useTranslation("global");
  const accessToken = useSelector(selectAuthToken);
  const { openModal } = useModal();

  const [showDetails, setShowDetails] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  // Rating states
  const [qualityPriceRating, setQualityPriceRating] = useState(0);
  const [locationRating, setLocationRating] = useState(0);
  const [servicesRating, setServicesRating] = useState(0);
  const [equipmentsRating, setEquipmentsRating] = useState(0);

  const { place, id: bookingId } = booking;
  const { booking_hours } = transformBookingData(booking);

  // Memoized derived values
  const hasAllRatings = useMemo(
    () => qualityPriceRating && locationRating && servicesRating && equipmentsRating,
    [qualityPriceRating, locationRating, servicesRating, equipmentsRating]
  );

  const averageRating = useMemo(() => {
    const total = qualityPriceRating + locationRating + servicesRating + equipmentsRating;
    const divisor = hasAllRatings ? 4 : 1;
    return Math.round(total / divisor);
  }, [qualityPriceRating, locationRating, servicesRating, equipmentsRating, hasAllRatings]);

  const toggleDetails = () => setShowDetails(!showDetails);

  const handleSubmitRatings = async () => {
    if (!hasAllRatings) return;

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const ratingData = {
        equipments: equipmentsRating,
        location: locationRating,
        quality_price: qualityPriceRating,
        quality_services: servicesRating,
      };

      const response = await submitSpaceRating(bookingId, ratingData, accessToken);

      // Show success message after a short delay
      setTimeout(() => {
        openModal({
          content: <p className={styles.successMessage}>{response.message}</p>,
          title: t("dashboard.ratings.myRating"),
          type: "success",
          autoClose: true,
          closeDelay: 3000,
        });
      }, 200);

      onRatingSubmitted(bookingId);
      setShowDetails(false);
      
      // Reset ratings
      setQualityPriceRating(0);
      setLocationRating(0);
      setServicesRating(0);
      setEquipmentsRating(0);
    } catch (error) {
      console.error("Rating submission failed:", error);
      setSubmitError(
        error.message || 
        t("dashboard.ratings.submitError") || 
        t("common.errors.generic")
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const openSpaceDetails = () => {
    window.open(`/space/${place.id}`, "_blank", "noopener,noreferrer");
  };

  return (
    <article className={styles.bookingCard} aria-labelledby={`booking-${bookingId}`}>
      <div className={styles.header}>
        {showDetails && (
          <CloseButton
            closeCallback={toggleDetails}
            className={styles.iconButton}
            aria-label={t("common.close")}
          />
        )}
      </div>

      <div className={styles.content}>
        <div className={styles.section1}>
          <div className={styles.images}>
            {place.images.slice(0, 1).map((image) => (
              <img
                key={image.id}
                src={image.thumb_image_url}
                alt={image.description || place.name}
                className={styles.image}
                loading="lazy"
              />
            ))}
          </div>

          <div className={styles.details}>
            <header className={styles.detailsHeader}>
              <h3 id={`booking-${bookingId}`} className={styles.title}>
                {place.name}
              </h3>
              <span className={styles.id}>
                {t("space.space")} {place.id}
              </span>
            </header>

            <div className={styles.detailsAddress}>
              <div className={styles.addressLine}>
                <span
                  className={styles.icon}
                  style={{ backgroundImage: `url(${pinSvgIconPink})` }}
                  aria-hidden="true"
                />
                <p>{`${place.address.address}, ${place.address.city}`}</p>
              </div>
            </div>

            <h4 className={styles.sectionTitle}>{t("basket.booking-overview")}</h4>

            <div className={styles.detailsMain}>
              <span className={styles.bookingInfo}>
                {bookingId} • {place.placeUsage.name} • {t("dashboard.ratings.rentedAt")}
              </span>
              {booking_hours.map((slot, idx) => (
                <p key={`${slot.from}-${idx}`} className={styles.timeSlot}>
                  {slot.date} {t("booking.step1.from")} {slot.from}{" "}
                  {t("booking.step1.to")} {slot.to}
                </p>
              ))}
            </div>
          </div>
        </div>

        {showDetails && (
          <section className={styles.ratingSection}>
            <div className={styles.bookingSummary}>
              <strong>{t("dashboard.ratings.myRating")}</strong>
            </div>

            <div className={styles.ratingsGrid}>
              {RATING_CATEGORIES.map((category) => {
                const ratingState = {
                  qualityPrice: qualityPriceRating,
                  location: locationRating,
                  services: servicesRating,
                  equipments: equipmentsRating,
                }[category.key];
                
                const setter = {
                  qualityPrice: setQualityPriceRating,
                  location: setLocationRating,
                  services: setServicesRating,
                  equipments: setEquipmentsRating,
                }[category.key];

                return (
                  <ReviewStarsInput
                    key={category.key}
                    title={t(category.translationKey)}
                    rating={ratingState}
                    setRating={setter}
                    disabled={isSubmitting}
                  />
                );
              })}
            </div>

            <div className={styles.averageRating}>
              {t("dashboard.ratings.ratingsAverage")}:{" "}
              <span className={styles.averageRatingValue}>
                {averageRating}/10
              </span>
            </div>

            {submitError && (
              <div className={styles.errorMessage} role="alert">
                {submitError}
              </div>
            )}
          </section>
        )}
      </div>

      <footer className={styles.footer}>
        {!showDetails ? (
          <div className={styles.buttonGroup}>
            <FormButton
              size="medium"
              onClick={toggleDetails}
              variant="yellow_textOnly"
              label={t("dashboard.ratings.myRating")}
              aria-expanded="false"
            />
            <FormButton
              size="medium"
              onClick={openSpaceDetails}
              variant="gray"
              label={t("dashboard.ratings.bookAgain")}
            />
          </div>
        ) : (
          <FormButton
            size="medium"
            onClick={handleSubmitRatings}
            label={
              isSubmitting ? t("buttons.submitting") : t("buttons.validate")
            }
            disabled={isSubmitting || !hasAllRatings}
            aria-busy={isSubmitting}
          />
        )}
      </footer>
    </article>
  );
};

export default SpaceToRateCard;